import React from 'react';
import styled from '@emotion/styled';
import { Controller } from 'react-hook-form';
import Board from '@/pages/Post/components/Board';
import SeminarThumbnailField from '@/pages/Seminar/components/form/SeminarThumbnailField';
import { ErrorMessage, FORM_COLOR, FieldBlock, GuideLines, Label } from '@/pages/Seminar/components/form/formStyles';
import { useSeminarForm } from '@/pages/Seminar/components/form/steps/SeminarFormContext';

/** 설명 에디터 — 상자형(반경 12)으로, 폰에서는 도구 줄을 작게 */
const EditorWrapper = styled.div<{ hasError?: boolean }>`
  border: 1px solid ${({ hasError }) => (hasError ? FORM_COLOR.error : FORM_COLOR.border)};
  border-radius: 12px;
  overflow: hidden;
  background: #fff;

  > div {
    box-shadow: none !important;
    border-radius: 0 !important;
  }

  > div > div {
    border: none !important;
    padding: 0 !important;
    background-color: transparent !important;
  }

  .quill {
    border: none !important;
    background-color: #fff;
  }

  .ql-toolbar {
    border: none !important;
    border-bottom: 1px solid ${FORM_COLOR.lineSoft} !important;
    border-radius: 0 !important;
    background-color: #fafbfa;
    padding: 10px !important;

    @media (max-width: 768px) {
      padding: 4px 6px !important;

      .ql-formats {
        margin-right: 6px !important;
      }
      button {
        width: 24px !important;
        height: 24px !important;
        padding: 2px !important;
      }
      .ql-picker {
        font-size: 12px !important;
        height: 24px !important;
      }
      .ql-picker-label {
        padding: 0 4px !important;
      }
      svg {
        width: 16px !important;
        height: 16px !important;
      }
    }
  }

  .ql-container {
    border: none !important;
    border-radius: 0 !important;
    font-size: 1rem;
    min-height: 300px;

    @media (max-width: 768px) {
      min-height: 240px;
    }
  }

  .ql-snow {
    border: none !important;
  }

  .ql-editor {
    padding: 16px;
    min-height: 300px;

    @media (max-width: 768px) {
      padding: 14px;
      min-height: 240px;
    }

    &::before {
      color: #9ca3af;
      font-style: normal;
    }
  }
`;

/** 4단계 — 썸네일(16:9 올리기·크롭)·설명(에디터) */
const StepIntro: React.FC = () => {
  const { control, errors, career, editorKey, showAlert } = useSeminarForm();

  return (
    <>
      <FieldBlock data-field="thumbnailUrl">
        <Label as="div">
          썸네일<span>*</span>
          <small>16:9</small>
        </Label>
        <Controller
          name="thumbnailUrl"
          control={control}
          render={({ field }) => (
            <SeminarThumbnailField value={field.value || null} onChange={(url) => field.onChange(url ?? '')} />
          )}
        />
        {errors.thumbnailUrl && <ErrorMessage>{errors.thumbnailUrl.message}</ErrorMessage>}
      </FieldBlock>

      <FieldBlock>
        <Label as="div">설명<span>*</span></Label>
        <EditorWrapper data-field="description" hasError={!!errors.description}>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <Board
                key={editorKey}
                onChange={field.onChange}
                defaultValue={field.value}
                onError={showAlert}
                // 긴 포스터는 서버가 위쪽 파생본(_top.jpg)을 만들어 상세 접힌 상태에서 원본 대신 보여 준다
                uploadPurpose="seminar-description"
              />
            )}
          />
        </EditorWrapper>
        {errors.description && <ErrorMessage>{errors.description.message}</ErrorMessage>}
        <GuideLines>
          {career
            ? '내용·커리큘럼·대상자를 적어 주세요.'
            : '내용과 포스터를 올리고, 문의처·접수 방법도 적어 주세요.'}
        </GuideLines>
      </FieldBlock>
    </>
  );
};

export default StepIntro;
